import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_VIOLET } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/heroes.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/heroes.mp3',
  attribution: '"Heroes" by RetroVision [NCS Release] - https://ncs.io/Heroes',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('heroes', variant),
    slug: buildVariantSlug('heroes', variant),
    title: buildVariantTitle('Heroes', variant),
    description:
      'Mid-tempo future house. 128 BPM with a syncopated lead riff - alternates groove and off-beat accents through the drop.',
    bpm: onsets.bpm,
    difficulty: 3,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_VIOLET,
    audio,
  });

  // 60s @ 128 BPM ≈ 128 beats. Two-drop structure with a syncopated bridge.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 48, kind: 'drop' });
  builder.section({ startBeat: 48, endBeat: 64, kind: 'bridge' });
  builder.section({ startBeat: 64, endBeat: 128, kind: 'drop' });

  // Verse: every kick. Establish the pulse.
  const versePads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['kick'],
    minSpacing: 0.75,
  });

  // First drop: kick + snare combo. Heroes has lots of kicks (82), so density
  // here is naturally higher than in Sky High.
  const drop1 = variantSelectBeats(variant, onsets, {
    fromBeat: 16,
    untilBeat: 48,
    kinds: ['kick', 'snare'],
    minIntensity: 0.3,
    minSpacing: 0.5,
  });

  // Bridge: syncopated lead notes only. Different rhythmic feel - leads land
  // off the beat in this section, breaking the kick pulse for variety.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 48,
    untilBeat: 64,
    kinds: ['lead'],
    minIntensity: 0.4,
    minSpacing: 0.75,
  });

  // Second drop: drops in a 16th-note "stutter" cluster every 4 beats by
  // keeping minSpacing tighter and including snare backbeats.
  const drop2 = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 126,
    kinds: ['kick', 'snare'],
    minIntensity: 0.3,
    minSpacing: 0.5,
  });

  const padBeats = mergeBeats(versePads, drop1, bridgePads, drop2);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 126,
    minGapBeats: 6,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(128);
  return builder.build();
}

export const HEROES_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const HEROES_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(HEROES_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
